import { PrintArea } from '@/web-components/import-wizard-v2/typings/common';
import { AnnotationShape } from '@/web-components/import-wizard/typings';
import { Collider2d, Polygon, Vector } from 'collider2d';
import CollisionDetails from 'collider2d/build/collision_details';
import inside from 'point-in-ellipse';

export function checkIsInArea(
  shape: AnnotationShape,
  area: any,
  areaType: string,
  areaRotation: number,
  lineWidth: number
): boolean {
  const collider2d = new Collider2d();
  const x = shape.x;
  const y = shape.y;
  const w = shape.width;
  const h = shape.height;
  const isRoundLogo = shape.isRoundLogo;

  const position = new Vector(x + w / 2, y + h / 2);
  const shapeRotation = shape.rotation;

  let shapeAreaPolygon = new Polygon();

  if (isRoundLogo) {
    const shapePolygon = createPolygonFromEllipse(
      new Vector(x + w / 2, y + h / 2),
      w / 2,
      h / 2,
      36
    );

    for (const point of shapePolygon.points) {
      point.x = point.x - position.x;
      point.y = point.y - position.y;
    }

    shapeAreaPolygon = new Polygon(position, shapePolygon.points);
  } else {
    shapeAreaPolygon = new Polygon(position, [
      new Vector(-w / 2, -h / 2),
      new Vector(-w / 2, h / 2),
      new Vector(w / 2, h / 2),
      new Vector(w / 2, -h / 2),
    ]);
  }

  shapeAreaPolygon.rotate(shapeRotation);

  let finalResult = false;

  if (areaType == 'Rectangle') {
    // add line width to prevent causing out of boundaries when logo is placed on lines
    const x = area.x;
    const y = area.y;
    const w = area.w;
    const h = area.h;

    const position = new Vector(x + w / 2, y + h / 2);
    const printableAreaPolygon = new Polygon(position, [
      new Vector(-w / 2, -h / 2),
      new Vector(-w / 2, h / 2),
      new Vector(w / 2, h / 2),
      new Vector(w / 2, -h / 2),
    ]);
    printableAreaPolygon.rotate(areaRotation);

    const { aInB } = collider2d.testPolygonPolygon(
      shapeAreaPolygon,
      printableAreaPolygon,
      true
    ) as CollisionDetails;

    finalResult = aInB || false;
  }
  if (areaType == 'Polygon') {
    const areaPolygon: Vector[] = [];
    for (let i = 0; i < area.length - 1; i++) {
      areaPolygon.push(new Vector(area[i].x, area[i].y));
    }

    const shapePoints = shapeAreaPolygon.points;

    for (const point of shapePoints) {
      point.add(position);
    }

    const isIn = polyPoly(areaPolygon, shapePoints);
    const isNOTOnLine = polyCollision(areaPolygon, shapePoints);

    finalResult = isIn ? isNOTOnLine : isIn;
  }

  if (areaType == 'Ellipse') {
    const polygonPoints = shapeAreaPolygon.points;
    const pointInEllipse = [];
    for (const point of polygonPoints) {
      pointInEllipse.push(
        inside(
          point.x + position.x,
          point.y + position.y,
          area.x,
          area.y,
          area.rx,
          area.ry,
          areaRotation
        )
      );
    }
    let pointsInEllipse = true;
    for (const onePointInEllipse of pointInEllipse) {
      if (!onePointInEllipse) {
        pointsInEllipse = false;
        break;
      }
    }

    finalResult = pointsInEllipse;
  }

  return finalResult;
}

// values for rotations should be in radians
export function checkIsInAreaV2(
  shape: AnnotationShape,
  area: PrintArea,
  areaRotation: number,
  lineWidth: number
): boolean {
  const collider2d = new Collider2d();
  const x = shape.x;
  const y = shape.y;
  const w = shape.width;
  const h = shape.height;
  const isRoundLogo = shape.isRoundLogo;

  const position = new Vector(x + w / 2, y + h / 2);

  let shapeAreaPolygon = new Polygon();

  if (isRoundLogo) {
    const shapePolygon = createPolygonFromEllipse(
      new Vector(x + w / 2, y + h / 2),
      w / 2,
      h / 2,
      36
    );

    for (const point of shapePolygon.points) {
      point.x = point.x - position.x;
      point.y = point.y - position.y;
    }

    shapeAreaPolygon = new Polygon(position, shapePolygon.points);
  } else {
    shapeAreaPolygon = new Polygon(position, [
      new Vector(-w / 2, -h / 2),
      new Vector(-w / 2, h / 2),
      new Vector(w / 2, h / 2),
      new Vector(w / 2, -h / 2),
    ]);
  }

  shapeAreaPolygon.rotate(shape.rotation);

  let finalResult = false;

  if (area.kind === 'Rectangle') {
    // add line width to prevent causing out of boundaries when logo is placed on lines
    const x = area.x + lineWidth / 2;
    const y = area.y + lineWidth / 2;
    const w = area.width - lineWidth;
    const h = area.height - lineWidth;

    const position = new Vector(x + w / 2, y + h / 2);
    const printableAreaPolygon = new Polygon(position, [
      new Vector(-w / 2, -h / 2),
      new Vector(-w / 2, h / 2),
      new Vector(w / 2, h / 2),
      new Vector(w / 2, -h / 2),
    ]);

    printableAreaPolygon.rotate(areaRotation);

    const { aInB } = collider2d.testPolygonPolygon(
      shapeAreaPolygon,
      printableAreaPolygon,
      true
    ) as CollisionDetails;
    finalResult = aInB || false;
  }
  if (area.kind === 'Polygon') {
    const areaPolygon: Vector[] = [];
    for (let i = 0; i < area.points.length - 1; i++) {
      areaPolygon.push(new Vector(area.points[i].x, area.points[i].y));
    }

    const shapePoints = shapeAreaPolygon.points;

    for (const point of shapePoints) {
      point.add(position);
    }

    const isIn = polyPoly(areaPolygon, shapePoints);
    const isNOTOnLine = polyCollision(areaPolygon, shapePoints);

    finalResult = isIn ? isNOTOnLine : isIn;
  }

  if (area.kind === 'Ellipse') {
    const polygonPoints = shapeAreaPolygon.points;

    const pointInEllipse = [];
    for (const point of polygonPoints) {
      pointInEllipse.push(
        inside(
          point.x + position.x,
          point.y + position.y,
          area.x,
          area.y,
          area.rx,
          area.ry,
          areaRotation
        )
      );
    }
    let pointsInEllipse = true;
    for (const onePointInEllipse of pointInEllipse) {
      if (!onePointInEllipse) {
        pointsInEllipse = false;
        break;
      }
    }

    finalResult = pointsInEllipse;
  }

  return finalResult;
}

function polyCollision(p1: Vector[], p2: Vector[]): boolean {
  let next = 0;
  let current = 0;
  for (current = 0; current < p1.length; current++) {
    next = current + 1;
    if (next == p1.length) next = 0;

    const vc: Vector = p1[current]; // c for "current"
    const vn: Vector = p1[next]; // n for "next"

    const collision = polyLine(p2, vc.x, vc.y, vn.x, vn.y);
    if (collision) return false;
  }

  return true;
}

function polyPoly(p1: Vector[], p2: Vector[]): boolean {
  let next = 0;
  let current = 0;
  for (current = 0; current < p1.length; current++) {
    next = current + 1;
    if (next == p1.length) next = 0;

    const p2inp1 = polyPoint(p1, p2[0].x, p2[0].y);
    if (p2inp1) return true;
  }

  return false;
}

function polyLine(
  vertices: Vector[],
  x1: number,
  y1: number,
  x2: number,
  y2: number
) {
  let next = 0,
    current = 0;
  for (current = 0; current < vertices.length; current++) {
    next = current + 1;
    if (next == vertices.length) next = 0;

    const x3 = vertices[current].x;
    const y3 = vertices[current].y;
    const x4 = vertices[next].x;
    const y4 = vertices[next].y;

    const hit = lineLine(x1, y1, x2, y2, x3, y3, x4, y4);
    if (hit) {
      return true;
    }
  }
  return false;
}

function lineLine(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  x4: number,
  y4: number
) {
  const uA =
    ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) /
    ((y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1));
  const uB =
    ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) /
    ((y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1));

  if (uA >= 0 && uA <= 1 && uB >= 0 && uB <= 1) {
    return true;
  }
  return false;
}

function polyPoint(vertices: Vector[], px: number, py: number) {
  let collision = false;

  let next = 0,
    current = 0;
  for (current = 0; current < vertices.length; current++) {
    next = current + 1;
    if (next == vertices.length) next = 0;

    const vc = vertices[current]; // c for "current"
    const vn = vertices[next]; // n for "next"

    if (
      ((vc.y > py && vn.y < py) || (vc.y < py && vn.y > py)) &&
      px < ((vn.x - vc.x) * (py - vc.y)) / (vn.y - vc.y) + vc.x
    ) {
      collision = !collision;
    }
  }
  return collision;
}

export function isConvex(polygon: Polygon): boolean {
  const points = [...polygon.points];
  if (points.length < 3) return false;

  let p;
  let v;
  let u;
  let result = 0;
  for (let i = 0; i < points.length; i++) {
    p = points[i];
    const temp = points[(i + 1) % points.length];
    v = new Vector();
    v.x = temp.x - p.x;
    v.y = temp.y - p.y;
    u = points[(i + 2) % points.length];

    if (i == 0) result = u.x * v.y - u.y * v.x + v.x * p.y - v.y * p.x;
    else {
      const newResult = u.x * v.y - u.y * v.x + v.x * p.y - v.y * p.x;
      if ((newResult > 0 && result < 0) || (newResult < 0 && result > 0))
        return false;
    }
  }
  return true;
}

export function createPolygonFromEllipse(
  center: Vector,
  radiusX: number,
  radiusY: number,
  numberOfPoints: number
): Polygon {
  const polygon = new Polygon();

  for (let i = 0; i < numberOfPoints; i++) {
    const percentDone = i / numberOfPoints;
    const currentEllipseAngle = percentDone * 2 * Math.PI;
    polygon.points.push(
      new Vector(
        radiusX * Math.cos(currentEllipseAngle) + center.x,
        radiusY * Math.sin(currentEllipseAngle) + center.y
      )
    );
  }

  return polygon;
}
